import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ErrorState } from "@/components/common/ErrorState";
import { HeroStatusCard } from "@/components/dashboard/HeroStatusCard";
import { MotivationCard } from "@/components/dashboard/MotivationCard";
import { MiniStats } from "@/components/dashboard/MiniStats";
import { QuietDayNudge } from "@/components/dashboard/QuietDayNudge";
import { useProfile } from "@/context/ProfileContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { useStats } from "@/hooks/useStats";
import { fadeUp } from "@/lib/motion";

export default function Dashboard() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { t, locale } = useLanguage();
  const { stats, loading, error, reload } = useStats();

  if (error) {
    return (
      <ErrorState
        message={t("dashboard.error")}
        onRetry={reload}
        testId="dashboard-retry-button"
      />
    );
  }

  const todayLabel = new Date().toLocaleDateString(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  const greeting = profile.name
    ? t("dashboard.greeting", { name: profile.name })
    : t("dashboard.greetingFallback");

  return (
    <div className="space-y-4">
      <motion.header {...fadeUp(0)} className="pt-2">
        <p className="text-xs uppercase tracking-[0.22em] text-cyan-300/70 font-medium">
          {todayLabel}
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">{greeting}</h1>
      </motion.header>

      <motion.div {...fadeUp(0.06)}>
        <HeroStatusCard stats={stats} loading={loading} />
      </motion.div>

      {/* Stage 2.2: streak-protection reminder (renders nothing unless at risk) */}
      <QuietDayNudge stats={stats} loading={loading} onAdd={() => navigate("/add")} />

      <motion.div {...fadeUp(0.12)}>
        <MotivationCard stats={stats} loading={loading} />
      </motion.div>

      <motion.div {...fadeUp(0.18)}>
        <MiniStats stats={stats} loading={loading} />
      </motion.div>

      <motion.div {...fadeUp(0.24)}>
        <Button
          data-testid="dashboard-add-movement-button"
          onClick={() => navigate("/add")}
          className="w-full py-6 rounded-xl bg-cyan-300 text-slate-950 text-base font-semibold hover:bg-cyan-200 active:scale-[0.98] shadow-[0_0_0_1px_rgba(34,211,238,0.25),0_12px_40px_rgba(34,211,238,0.15)]"
        >
          <Plus size={20} className="mr-1" /> {t("common.addMovement")}
        </Button>
      </motion.div>
    </div>
  );
}
